import React, { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Slot from "./Slot";
import ExportPdfButton from "./ExportPdfButton";
import ScorePopup from "./ScorePopup";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import {
  advance,
  roundLabel,
  cX,
  cY,
  MW,
  MH,
  VG,
  HG,
  PAD,
} from "../../utils/bracketLogic";
import "./BracketScreen.css";

const CW = 160;

function BracketScreen({
  initRounds,
  tournament,
  onReshuffle,
  onSeededShuffle,
  onReset,
  onComplete,
  backLabel = "← New tournament",
}) {
  const { tournamentsApi, matchResultsApi } = useApi();
  const { flags } = useFeatures();
  const ratingsOn = flags.ratingsEnabled;
  const scoringOn = flags.scoringEnabled;
  const queryClient = useQueryClient();

  const [rounds, setRounds] = useState(initRounds);
  const [pending, setPending] = useState(null); // { r, m, winner, loser, x, y }
  const numR = rounds.length;
  const stageRef = useRef(null);

  const recordMatchMutation = useMutation({
    mutationFn: (payload) => matchResultsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matchResults"] });
      queryClient.invalidateQueries({ queryKey: ["players"] });
    },
  });

  const finalizeTournamentMutation = useMutation({
    mutationFn: ({ id, body }) => tournamentsApi.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });

  const champion = rounds[numR - 1][0].winner;
  // "Started" once any real (non-bye) match has been decided — locks reshuffle.
  const started = rounds.some((round) =>
    round.some((m) => m.team1 && m.team2 && m.winner),
  );

  const svgW = PAD + numR * (MW + HG) + CW + PAD;
  const svgH = PAD + rounds[0].length * (MH + VG) - VG + PAD;

  const champX = cX(numR);
  const champY = cY(numR - 1, 0);

  React.useEffect(() => {
    setRounds(initRounds);
  }, [initRounds]);

  // Auto-save the live bracket so it can be resumed from the Tournaments tab.
  React.useEffect(() => {
    if (!tournament?.id) return;
    Promise.resolve(
      tournamentsApi.update(tournament.id, {
        ...tournament,
        status: 1,
        format: "single",
        bracketState: JSON.stringify({ format: "single", rounds }),
      }),
    ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds]);

  function requestPick(r, m, winner, e) {
    const match = rounds[r][m];
    const loser = match.team1 === winner ? match.team2 : match.team1;
    setPending({ r, m, winner, loser, x: e.clientX, y: e.clientY });
  }

  function commitPick(winnerScore, loserScore) {
    const { r, m, winner, loser } = pending;
    setPending(null);
    const match = rounds[r][m];

    const next = advance(rounds, r, m, winner);
    // Store scores in team-position order on the match.
    if (match.team1 === winner) {
      next[r][m].s1 = winnerScore;
      next[r][m].s2 = loserScore;
    } else {
      next[r][m].s1 = loserScore;
      next[r][m].s2 = winnerScore;
    }
    setRounds(next);

    if (winner && loser && tournament?.id) {
      recordMatchMutation.mutate({
        winnerName: winner,
        loserName: loser,
        tournamentId: tournament.id,
        tournamentName: tournament.name,
        roundNumber: r,
        matchIndex: m,
        winnerScore,
        loserScore,
      });
    }

  }

  function completeTournament() {
    if (!champion || !tournament?.id) return;
    const finalMatch = rounds[numR - 1][0];
    const runnerUp =
      finalMatch.team1 === champion ? finalMatch.team2 : finalMatch.team1;
    finalizeTournamentMutation.mutate(
      {
        id: tournament.id,
        body: {
          ...tournament,
          status: 2,
          winner: champion,
          runnerUp: runnerUp || "",
          format: "single",
          bracketState: JSON.stringify({ format: "single", rounds }),
        },
      },
      { onSuccess: () => onComplete && onComplete() },
    );
  }

  return (
    <div>
      <div className="tn-bhead">
        <span className="tn-btitle">BRACKET</span>
        {tournament?.name && (
          <span className="tn-btournament">{tournament.name}</span>
        )}
        {onReshuffle && (
          <button
            className="tn-ghost"
            onClick={onReshuffle}
            disabled={started || !!champion}
            title={
              started
                ? "Cannot reshuffle once matches have been played"
                : "Random shuffle"
            }
          >
            ↺ Random
          </button>
        )}
        {onSeededShuffle && ratingsOn && (
          <button
            className="tn-ghost"
            onClick={onSeededShuffle}
            disabled={started || !!champion}
            title={
              started
                ? "Cannot reshuffle once matches have been played"
                : "Seeded shuffle by rating"
            }
          >
            ↺ Seeded
          </button>
        )}
        <ExportPdfButton targetRef={stageRef} filename={tournament?.name} />
        <button className="tn-ghost" onClick={onReset}>
          {backLabel}
        </button>
      </div>

      <div className="tn-bscroll">
        <div className="tn-rlabels" style={{ marginLeft: PAD }}>
          {rounds.map((round, r) => (
            <div key={r} className="tn-rlabel" style={{ width: MW + HG }}>
              {roundLabel(numR - 1 - r, round.length)}
            </div>
          ))}
          <div className="tn-rlabel" style={{ width: CW }}>
            Champion
          </div>
        </div>

        <div
          ref={stageRef}
          style={{ position: "relative", width: svgW, height: svgH }}
        >
          <svg
            style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            width={svgW}
            height={svgH}
          >
            {rounds.slice(0, -1).map((round, r) =>
              round.map((match, m) => {
                const x1 = cX(r) + MW,
                  y1 = cY(r, m);
                const x2 = cX(r + 1),
                  y2 = cY(r + 1, m >> 1);
                const mx = x1 + HG / 2;
                return (
                  <polyline
                    key={`${r}-${m}`}
                    points={`${x1},${y1} ${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                    fill="none"
                    stroke={
                      match.winner
                        ? "var(--color-won-border)"
                        : "var(--color-border)"
                    }
                    strokeWidth={2}
                  />
                );
              }),
            )}
            {(() => {
              const x1 = cX(numR - 1) + MW;
              const mx = x1 + HG / 2;
              return (
                <polyline
                  points={`${x1},${champY} ${mx},${champY} ${mx},${champY} ${champX},${champY}`}
                  fill="none"
                  stroke={
                    champion ? "var(--color-won-border)" : "var(--color-border)"
                  }
                  strokeWidth={2}
                />
              );
            })()}
          </svg>

          {rounds.map((round, r) =>
            round.map((match, m) => {
              const { team1, team2, winner } = match;
              const canPick = !!team1 && !!team2 && !winner;
              return (
                <div
                  key={`${r}-${m}`}
                  className={`tn-match${winner ? " won" : ""}`}
                  style={{
                    left: cX(r),
                    top: cY(r, m) - MH / 2,
                    width: MW,
                    height: MH,
                  }}
                >
                  <Slot
                    team={team1}
                    isWinner={winner === team1 && team1 !== null}
                    canPick={canPick}
                    onPick={(e) => requestPick(r, m, team1, e)}
                    score={match.s1}
                  />
                  <div className="tn-divider" />
                  <Slot
                    team={team2}
                    isWinner={winner === team2 && team2 !== null}
                    canPick={canPick}
                    onPick={(e) => requestPick(r, m, team2, e)}
                    score={match.s2}
                  />
                </div>
              );
            }),
          )}

          <div
            className={`tn-champ-slot${champion ? " filled" : ""}`}
            style={{
              left: champX,
              top: champY - MH / 2,
              width: CW,
              height: MH,
            }}
          >
            {champion ? (
              <>
                <span className="tn-champ-name">{champion}</span>
              </>
            ) : (
              <span className="tn-champ-empty">TBD</span>
            )}
          </div>
        </div>
      </div>

      <div className="tn-complete-bar">
        <button
          className="tn-complete-btn"
          onClick={completeTournament}
          disabled={!champion}
          title={
            champion
              ? "Mark this tournament complete"
              : "Finish all matches first"
          }
        >
          Complete Tournament
        </button>
      </div>

      {pending && (
        <ScorePopup
          winner={pending.winner}
          loser={pending.loser}
          scoringEnabled={scoringOn}
          position={{ x: pending.x, y: pending.y }}
          onConfirm={commitPick}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

export default BracketScreen;
